import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { ITraining } from '../../../../../../domains/Training';

import {
  DocTitle,
  ExercisesTable,
  Footer,
  GymIdentity,
  Header,
  Sheet,
  SplitBlock,
  StudentInfo,
} from './styles';

interface IPrintSplit {
  label: string;
  trainings: ITraining[];
}

interface IPrintWorkoutSheetProps {
  gymName?: string;
  gymLogoUrl?: string;
  studentName: string;
  splits: IPrintSplit[];
  lastEditedByName?: string | null;
}

const sanitizeNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return value;
};

const formatRange = (
  minInput?: number | null,
  maxInput?: number | null,
  suffix = '',
): string => {
  const min = sanitizeNumber(minInput);
  const max = sanitizeNumber(maxInput);

  if (min === undefined && max === undefined) return '—';
  if (max === undefined || min === max) return `${min}${suffix}`;
  return `${min}-${max}${suffix}`;
};

const formatMuscleGroups = (training: ITraining): string =>
  training.exercise.muscle_groups
    .map(group => group.muscle_group?.name || group.name)
    .filter(Boolean)
    .join(', ') || '—';

const PrintWorkoutSheet: React.FC<IPrintWorkoutSheetProps> = ({
  gymName,
  gymLogoUrl,
  studentName,
  splits,
  lastEditedByName,
}) => {
  return (
    <Sheet className="print-sheet">
      <Header>
        <GymIdentity>
          {gymLogoUrl && <img src={gymLogoUrl} alt={gymName} />}
          {gymName && <span>{gymName}</span>}
        </GymIdentity>
        <DocTitle>
          <h1>Ficha de Treino</h1>
          <p>
            Gerada em{' '}
            {format(new Date(), "dd/MM/yyyy 'às' HH:mm", {
              locale: ptBR,
            })}
          </p>
        </DocTitle>
      </Header>

      <StudentInfo>
        <h2>{studentName}</h2>
        {!!lastEditedByName && <p>Montado por {lastEditedByName}</p>}
      </StudentInfo>

      {splits.map(split => (
        <SplitBlock key={split.label}>
          <h3>Split {split.label}</h3>
          <ExercisesTable>
            <thead>
              <tr>
                <th>Exercício</th>
                <th>Grupo muscular</th>
                <th>Séries</th>
                <th>Repetições</th>
                <th>Descanso</th>
                <th>Observações</th>
              </tr>
            </thead>
            <tbody>
              {split.trainings.map(training => (
                <tr key={training.id}>
                  <td>{training.exercise.name}</td>
                  <td>{formatMuscleGroups(training)}</td>
                  <td>{formatRange(training.sets_min, training.sets_max)}</td>
                  <td>{formatRange(training.reps_min, training.reps_max)}</td>
                  <td>
                    {formatRange(
                      training.interval_min,
                      training.interval_max,
                      's',
                    )}
                  </td>
                  <td>{training.additional_information || '—'}</td>
                </tr>
              ))}
            </tbody>
          </ExercisesTable>
        </SplitBlock>
      ))}

      <Footer>Gerado por TrenzoX</Footer>
    </Sheet>
  );
};

export { PrintWorkoutSheet };
